import { Directive, effect, ElementRef, inject, InjectionToken, Provider, input } from '@angular/core';
import { BrnTooltip, BrnTooltipPosition, provideBrnTooltipDefaultOptions } from '@spartan-ng/brain/tooltip';
import { hlm } from '@lite-workspace/shared/helm-ui/utils';
import { IS_MOBILE } from '@lite-workspace/utils';
import { cva } from 'class-variance-authority';

// The tip is this svg's OWN box, not the polygon Brain draws inside it: an 8px square turned 45 degrees and
// painted with the plate colour, its far half sticking out. So the hairline is a plain CSS border on the two
// sides that end up facing out (`r` and `b` after the turn) — no stroke on the squashed 30x10 -> 10x5
// polygon, which is why the earlier dashed-stroke attempts came out uneven.
// Two things keep the line continuous. The square is centred ON the plate's hairline, so its near half lies
// over the plate and hides the plate's own line across the base, while its side corners land exactly where
// that line resumes. And only the tip corner is rounded: rounding the side corners would taper each border
// away before it reached the plate and leave a notch there.
export const DEFAULT_TOOLTIP_SVG_CLASS =
  'bg-menu-surface fill-menu-surface z-50 block size-2 translate-y-[var(--inset-tooltip-arrow)] rotate-45 ' +
  'rounded-br-xs border-r border-b border-[var(--hairline-on-dark)]';

export const DEFAULT_TOOLTIP_CONTENT_CLASSES = hlm(
  'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in ' +
    'data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out ' +
    'data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 ' +
    'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 ' +
    'data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-90 origin-(--radix-tooltip-content-transform-origin) ' +
    // `w-fit` hugs the text and wraps only past `max-w-90` (360px: a 2–3 sentence hint in ~2 lines); NO `text-balance` — with fit-content it sizes the
    // box to the cap before balancing the lines, leaving dead space to the right of short wrapped copy.
    // The plate is dark in both themes, so on a dark editor surface it needs the same lift every other
    // floating surface here gets — hairline plus shadow — or its edge disappears. The hairline is the
    // on-dark ink, not a theme line token: those invert with the theme while this plate never does.
    // `pre-line` вместо `normal`: перенос по ширине тот же, но подсказка из двух строк ставит их через `\n`.
    'rounded-md px-3 py-1.5 text-xs bg-menu-surface text-on-menu-surface whitespace-pre-line ' +
    'border border-[var(--hairline-on-dark)] shadow-[var(--shadow-3)]',
);

const TOOLTIP_SHOW_DELAY_MS = 500;
/** Short grace on leave so a pointer clipping the plate's edge doesn't blink it out and back. */
const TOOLTIP_HIDE_DELAY_MS = 80;

/**
 * Dwell before a tip appears. It is a token, not a plain constant, because this directive carries Brain's
 * options on its own element injector — an app-level `provideBrnTooltipDefaultOptions` never reaches a
 * trigger, the element answers first. A cabinet whose hints are read at a glance provides its own value;
 * every other product keeps the sweep guard above.
 */
export const HLM_TOOLTIP_SHOW_DELAY_MS = new InjectionToken<number>('HLM_TOOLTIP_SHOW_DELAY_MS', {
  providedIn: 'root',
  factory: () => TOOLTIP_SHOW_DELAY_MS,
});

export function provideHlmTooltipShowDelay(showDelayMs: number): Provider {
  return { provide: HLM_TOOLTIP_SHOW_DELAY_MS, useValue: showDelayMs };
}

// Brain keeps its options token private; its own provider factory is the only handle on it, so the styling
// half is built once here and the dwell is layered on per injector below.
const brnTooltipOptions = provideBrnTooltipDefaultOptions({
  // A pointer sweeping a toolbar crosses a dozen triggers: without a dwell they all flash in turn.
  showDelay: TOOLTIP_SHOW_DELAY_MS,
  hideDelay: TOOLTIP_HIDE_DELAY_MS,
  svgClasses: DEFAULT_TOOLTIP_SVG_CLASS,
  tooltipContentClasses: DEFAULT_TOOLTIP_CONTENT_CLASSES,
  arrowClasses: (position: BrnTooltipPosition) => hlm(tooltipPositionVariants({ position })),
});

export const tooltipPositionVariants = cva('absolute', {
  variants: {
    position: {
      top: 'bottom-0 left-[var(--inset-tooltip-center)] translate-y-full',
      bottom: '-top-2 left-[var(--inset-tooltip-center)] translate-y-0 rotate-180',
      left: '-end-2 top-[var(--inset-tooltip-center)] translate-y-0 rotate-270 rtl:-rotate-270',
      right: '-start-2 top-[var(--inset-tooltip-center)] translate-y-0 rotate-90 rtl:-rotate-90',
    },
  },
});

@Directive({
  selector: '[hlmTooltip]',
  exportAs: 'hlmTooltip',
  providers: [
    {
      provide: brnTooltipOptions.provide,
      useFactory: () => ({ ...brnTooltipOptions.useValue, showDelay: inject(HLM_TOOLTIP_SHOW_DELAY_MS) }),
    },
  ],
  hostDirectives: [
    {
      directive: BrnTooltip,
      inputs: ['brnTooltip: hlmTooltip', 'position', 'hideDelay', 'showDelay', 'tooltipDisabled'],
    },
  ],
  host: {
    '(pointerdown)': '_hideOnInteraction()',
    '(focus)': '_suppressFocusShowAfterPointer()',
    '(mouseenter)': '_onTriggerEnter()',
    '(mouseleave)': '_onTriggerLeave()',
  },
})
export class HlmTooltip {
  private readonly _brnTooltip = inject(BrnTooltip, { self: true });

  /** Status hints (no action behind the trigger) keep the plate on click instead of dismissing it. */
  public readonly interactionHide = input(true);

  // Hover tooltips are a pointer-device affordance — on phones they linger after taps and cover
  // controls, so the whole primitive is inert there; a hint that matters on mobile goes on-screen
  // or into a sheet, never re-enabled per trigger (R12 mobile-adaptive).
  constructor() {
    const isMobile = inject(IS_MOBILE);
    const brnTooltip = this._brnTooltip;
    effect(() => {
      brnTooltip.mutableTooltipDisabled.set(isMobile() ? true : brnTooltip.tooltipDisabled());
    });
  }

  private _lastPointerDownAt = 0;

  /**
   * Показать подсказку СРАЗУ, по клику. Единственный повод — отказавший контрол: наведение объясняет
   * отказ только тому, кто ждёт полсекунды, а тот, кто просто ткнул в серую кнопку, не получает ничего.
   * Наведения других подсказок это не касается: показ идёт только по прямому вызову.
   *
   * Клик приходит ПОСЛЕ `pointerdown`, который плашку уже погасил, поэтому показ мгновенный, а метка
   * pointerdown сбрасывается: иначе `_suppressFocusShowAfterPointer` снял бы ровно тот показ, ради
   * которого клик и был.
   */
  show(): void {
    this._lastPointerDownAt = 0;
    (this._brnTooltip as unknown as { delay(isShow: boolean, delay: number, instant: boolean): void }).delay(true, 0, true);
  }

  // A click answers the hint: the plate must not linger over the control being used, and the
  // focus the click leaves behind must not keep it alive. Brain exposes no public hide, so this
  // reaches its internal delay queue (same path its own blur/mouseleave listeners use).
  protected _hideOnInteraction(): void {
    if (!this.interactionHide()) return;
    this._host.removeEventListener('pointermove', this._showOnMove);
    this._lastPointerDownAt = Date.now();
    (this._brnTooltip as unknown as { delay(isShow: boolean): void }).delay(false);
  }

  protected _hovered = false;

  private readonly _mountedAt = Date.now();

  // Brain flags the plate as hovered on its host's mouseenter and blocks every hide while the flag is up.
  // The host leaves the DOM on detach, so a pointer that entered the plate during its exit never gets the
  // mouseleave and the flag stays up for good: the next plate then ignores leave, click and scroll. A pointer
  // on the trigger is not on the plate — clear the flag here, before Brain's own enter listener shows.
  // The browser also fires mouseenter on a trigger that mounts under a resting pointer (a dialog opening
  // under the cursor): an enter inside the show delay of mount is that, not a hover — drop its show.
  // A resting pointer emits no pointermove, a real entry always does: the first move re-queues the show.
  protected _onTriggerEnter(): void {
    this._hovered = true;
    (this._brnTooltip as unknown as { _tooltipHovered: boolean })._tooltipHovered = false;
    if (Date.now() - this._mountedAt < this._brnTooltip.showDelay()) {
      this._cancelQueuedShow();
      this._host.addEventListener('pointermove', this._showOnMove, { once: true });
    }
  }

  protected _onTriggerLeave(): void {
    this._hovered = false;
    this._host.removeEventListener('pointermove', this._showOnMove);
  }

  private readonly _host: HTMLElement = inject(ElementRef).nativeElement;

  // Deferred like the cancel, so a move in the same task as the enter lands after it.
  private readonly _showOnMove = (): void => {
    setTimeout(() => this._hovered && (this._brnTooltip as unknown as { _requestShow(): void })._requestShow());
  };

  // The plate is a hover affordance. Brain's own `focus` listener queues a show on ANY focus: a dialog
  // handing focus back to its trigger, the window regaining focus, a click. With no pointer on the trigger
  // no mouseleave ever comes, so that plate sticks. Cancel the queued show one macrotask later — after
  // Brain's listener has run — unless the pointer rests on the trigger and the focus is not click-born
  // (a click answers the hint, see `_hideOnInteraction`). `:hover` covers a focus moved from inside another
  // `mouseenter` (dropdown focus-on-hover): that listener runs before ours, so `_hovered` is still false.
  protected _suppressFocusShowAfterPointer(): void {
    if ((this._hovered || this._host.matches(':hover')) && Date.now() - this._lastPointerDownAt > 800) return;
    this._cancelQueuedShow();
  }

  private _cancelQueuedShow(): void {
    setTimeout(() => (this._brnTooltip as unknown as { delay(isShow: boolean): void }).delay(false));
  }
}
